import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { fetchAllInterests, fetchAthleteProfileByEmail } from "../../../../store/actions/athleteActions";
import { fetchAllCodes } from "../../../../store/actions/codeActions";
import { useCodeMaps } from "../../../../utils/utils";
import { Avatar, Badge, Btn, Card, ConfirmModal, ErrorState, Loading, PageHeader } from "../../../ForBrands/ui/BrandUI";
import { usePhoneTheme } from "../../../ForBrands/useIsPhone";
import "../../../auth/v2/onboarding.css";
import { SECTIONS, GROUPS, PRIVACY_ROWS, HISTORY_LISTS, sectionProgress, hubProgress, nextSection, cerebroFills, cerebroSchoolNames, identityHistoryNames, identitySchoolNames, matchCerebroPosition, trustedBio } from "./editHubModel";
import { Ring, NavIcon, SectionHead, SectionFooter, ProfileStrength } from "./editHubPieces";
import { PhoneEditFrame, PhoneHub, PhoneSection } from "./EditHubPhone";
import { saveProfileDoc, uploadAvatar, compressImage, deleteAvatar, recomputeSelf } from "./editHubServices";
import { PhotoBioSection, PersonalSection, SportSection, SchoolSection } from "./sections/EssentialsSections";
import { teamTitle } from "./sections/TeamsCard";
import { fillNoteOf } from "../../../auth/Onboarding/codeTeams";
import { SocialsSection } from "./sections/SocialsSection";
import { AcademicsSection, HonorsSection, WorkSection } from "./sections/ImpactSections";
import { PrivacySection } from "./sections/PrivacySection";

// Edit profile · Hub model (v2). The athlete's own editor: a sidebar with
// the overall strength and every section's progress, the open section on
// the right. Edits save on their own a moment after the last keystroke
// (and on Save, Next, switching sections and leaving the page).
//
// On phones in Athlete Mode (the phone theme) it is a stack of screens
// instead (EditHubPhone.js, D-e1): no `section` in the URL shows the hub
// list, a section opens as its own screen with the AutosaveBar, and Done
// saves what is pending and returns to the hub. The state and saving below
// are the same for both.
//
// With `editingEmail` it edits that athlete for a Circle member instead
// (CircleAthleteEdit): the profile loads by that email and saves through
// the same athlete/update/ call, where the server applies the member's
// Circle permission. What only the athlete can do from their own account
// is left out: the privacy settings, removing the photo, the team list and
// team codes, and the social follower lookup (it reads the signed-in
// account's own onboarding state).

const EMPTY_ARR = Object.freeze([]);
const EMPTY_OBJ = Object.freeze({});
const AUTOSAVE_MS = 1500;

// The save that ran when the athlete last left the hub. The next visit waits
// for it, so the fresh load it hydrates from already holds those edits.
let pendingFlush = Promise.resolve();

// Fields other endpoints own (team and coach codes, the roster claim,
// verification, onboarding, the score pipelines, the server's pre-fill
// marks, which history lists the athlete saved, whether the bio is
// trusted). The hub never edits them, so it never sends them back: a copy
// loaded before a code link or a recompute must not undo it.
const SERVER_OWNED = [
  "athleteId", "verifiedTeams", "coachName", "currentTeamName", "team_id", "league", "team_source", "teams", "player_id",
  "status", "onboardingCompleted", "coachEmail", "coachVerificationCode", "coachVerifiedAt", "teamCode", "teamCodeVerifiedAt",
  "statistics", "social_score", "isCalculating", "cohort", "identity", "bioTrusted", "historyEdited", "prefilled",
];
// After a code link, these come from the reloaded profile (the identity and
// the team list say where the linked team now shows, and what kind it is).
const TEAM_LINK_FIELDS = ["currentTeam", "team_id", "league", "team_source", "player_id", "teams", "coachEmail", "coachName", "status", "verifiedTeams", "onboardingCompleted", "identity", "allTeams"];
// Editor name -> the institution field it saves as. A pro's Team is
// clubTeam (currentTeam) and their University is school (previousSchool).
const NAME_FIELDS = {
  school: "previousSchool", clubTeam: "currentTeam", lastHighSchool: "lastHighSchool",
  previousClubTeams: "previousClubTeams", previousColleges: "previousColleges", previousHighSchools: "previousHighSchools",
};
const SINGLE_NAMES = ["school", "clubTeam", "lastHighSchool"];
const sameEmail = (x, y) => String(x || "").trim().toLowerCase() === String(y || "").trim().toLowerCase();
// ?section=teams opens the Teams section (its id is still "school").
const SECTION_ALIASES = { teams: "school" };

// Whether "back" can step back to a page of this app before the editor. The
// browser router numbers its history entries (idx 0 is the first page of the
// visit); without that (a memory router) it goes by whether the editor was
// the first entry, which the router keys "default".
const canStepBack = (enteredFirst) => {
  const idx = typeof window !== "undefined" ? window.history?.state?.idx : undefined;
  if (typeof idx === "number") return idx > 0;
  return !enteredFirst;
};
const scrollToY = (top) => {
  try { window.scrollTo({ top }); } catch { /* no scrolling here */ }
};
const scrollTop = () => scrollToY(0);
const pageScrollY = () => (typeof window === "undefined" ? 0 : window.scrollY || window.pageYOffset || 0);

// Sections a Circle member never sees, and the fields their saves leave
// out: the athlete's own privacy choices (the load fills in defaults, which
// must not be written on their behalf) and the team list the load adds.
const SELF_ONLY_SECTIONS = new Set(["privacy"]);
const SELF_ONLY_FIELDS = ["privacyPrefs", "showContactInfo", "allTeams"];
const PERMISSION_LABELS = { view: "View only", manage: "Manage", edit: "Edit" };

// Fills blanks from the Cerebro match so the athlete confirms instead of
// typing (editHubModel.cerebroFills, which the server's profile strength
// mirrors), only when the server trusts that match (trustedBio), plus the
// default privacy settings.
const hydrate = (info, sportCodes) => {
  const next = cerebroFills({ ...info, cerebroBio: trustedBio(info) }, sportCodes);
  if (!next.privacyPrefs) next.privacyPrefs = Object.fromEntries(PRIVACY_ROWS.map((r) => [r.key, r.defaultValue]));
  return next;
};

const levelOf = (a) => Number(a?.levelOfPlay) || 0;

// The names a load of `doc` (the profile as athlete/by-email sends it)
// shows in the Teams fields, and the ones saves leave out until the athlete
// edits them (`unresolved`, see unresolvedRef). `a` is the editor's copy of
// the profile (hydrate's), whose level says which field each team fills;
// `codeMap` names the stored codes. What the athlete saved comes first, then
// the server's identity (each team where its own kind puts it: a college a
// link filed in the Club field fills the college Team, never Club), then
// Cerebro by athlete type. With an identity the Club is its club alone. A
// stats doc the server does not trust fills nothing (trustedBio), from the
// identity either.
const loadedNames = (doc, a, codeMap) => {
  const unresolved = new Set();
  const trusted = doc.bioTrusted === true;
  const bio = trustedBio(doc);
  const nameOf = (type, v) => (typeof v === "string" ? v : codeMap[type]?.[v]?.name || "");
  const nameFor = (key, type, v) => {
    const name = nameOf(type, v);
    if (!name && v) unresolved.add(key);
    return name;
  };
  const namesFor = (key, type, list) => {
    const raw = (Array.isArray(list) ? list : []).filter(Boolean);
    const out = raw.map((v) => nameOf(type, v)).filter(Boolean);
    if (out.length < raw.length) unresolved.add(key);
    return out;
  };
  const identity = doc.identity?.current ? doc.identity : null;
  const fromIdentity = identitySchoolNames(a, identity, trusted);
  const fromBio = cerebroSchoolNames(a, bio);
  const history = identityHistoryNames(identity, trusted);
  // A history list the athlete has saved from here (the server's
  // historyEdited mark) shows what they saved and goes with every save.
  // Any other list shows what the profile shows, the identity's previous
  // teams (a list stored before the mark, plus what was found), display
  // only until the athlete edits it: the server takes a list as the
  // athlete's own once a save sends it, so sending one they never touched
  // would hide teams found for them that they never saw here, and no codes
  // are made for names we found. Without an identity the stored list shows.
  const edited = doc.historyEdited && typeof doc.historyEdited === "object" ? doc.historyEdited : {};
  const listFor = (key, type) => {
    const saved = namesFor(key, type, doc[NAME_FIELDS[key]]);
    if (unresolved.has(key) || edited[NAME_FIELDS[key]]) return saved;
    unresolved.add(key);
    return identity ? history[key] : saved;
  };
  // With no level yet, a Team only the identity gives (the team they are
  // linked to) is shown and counted but not sent until edited: a school
  // saved with no level is filed with no type, and the server files a
  // linked college itself (onboarding_views.college_school_set).
  const savedSchool = nameFor("school", "school", doc.previousSchool);
  if (!savedSchool && fromIdentity.school && !levelOf(a)) unresolved.add("school");
  const names = {
    school: savedSchool || fromIdentity.school || fromBio.school,
    clubTeam: identity ? fromIdentity.clubTeam : nameFor("clubTeam", "team", doc.currentTeam) || fromBio.clubTeam,
    lastHighSchool: nameFor("lastHighSchool", "school", doc.lastHighSchool) || fromIdentity.lastHighSchool || fromBio.lastHighSchool,
    previousClubTeams: listFor("previousClubTeams", "team"),
    previousColleges: listFor("previousColleges", "school"),
    previousHighSchools: listFor("previousHighSchools", "school"),
  };
  return { names, unresolved };
};

// What a new main team (Make primary) can change on the server besides the
// team fields: where its school or club is filed, and the history lists a
// transfer's old school moves to. The editor takes them from the reload.
const SLOT_FIELDS = ["previousSchool", "lastHighSchool", "previousColleges", "previousHighSchools", "previousClubTeams", "historyEdited"];

const AthleteEditHub = ({ editingEmail = "", circlePermission = "", backTo = "" }) => {
  const dispatch = useDispatch();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  // The phone screens (Athlete Mode below 640px). The Circle editor's shells
  // have no phone theme, so it keeps this layout on phones (D-e2).
  const phone = usePhoneTheme();
  const enteredFirstRef = useRef(location.key === "default");
  // True when a Circle member edits another athlete's profile.
  const editingOther = !!editingEmail;
  const email = editingEmail || localStorage.getItem("myEmail");
  const codes = useSelector((s) => s.codes?.allCodes || EMPTY_ARR);
  const athleteInfo = useSelector((s) => s.athlete?.data || EMPTY_OBJ);
  const interestsKnown = useSelector((s) => s.athlete?.interests || EMPTY_ARR);
  const codeMap = useCodeMaps(codes);

  const [athlete, setAthlete] = useState(null);
  // Hydrate only from this visit's own load, never from the copy an earlier
  // visit (or an earlier account) left in the store.
  const [fresh, setFresh] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [loadTry, setLoadTry] = useState(0);
  const [names, setNamesState] = useState({ school: "", clubTeam: "", lastHighSchool: "", previousClubTeams: [], previousColleges: [], previousHighSchools: [] });
  const [status, setStatus] = useState("saved");
  const [error, setError] = useState("");
  // The server's message per field when it refused the last save.
  const [fieldErrors, setFieldErrors] = useState({});
  const [avatarBusy, setAvatarBusy] = useState(false);
  const [confirmingRemove, setConfirmingRemove] = useState(false);
  // Done on a phone section while it saves what is pending.
  const [doneBusy, setDoneBusy] = useState(false);
  const aliveRef = useRef(true);
  const latest = useRef({ athlete: null, names });
  const dirtyRef = useRef(false);
  const timerRef = useRef(null);
  const savedOnceRef = useRef(false);
  // The last save request sent (settles true or false, never rejects).
  const inFlightRef = useRef(null);
  // True while that request is still on its way.
  const busyRef = useRef(false);
  const positionsFilledRef = useRef(false);
  // Names left out of saves until the athlete edits them: a stored code the
  // code list lacks (saving the blank name would erase it), a name a code
  // link already saved, the Teams names a new main team filed on the server
  // (Make primary), and a history list they have not saved from here
  // (display only, so no codes are made for names we found and a saved
  // list, which the server then takes as the athlete's own, is one they
  // chose).
  const unresolvedRef = useRef(new Set());
  const touchedRef = useRef(new Set());
  latest.current = { athlete, names };

  useEffect(() => {
    if (!email) return undefined;
    let live = true;
    setLoadFailed(false);
    pendingFlush
      .then(() => (live ? dispatch(fetchAthleteProfileByEmail(email)) : undefined))
      .then((doc) => {
        if (!live) return;
        if (doc === null) setLoadFailed(true);
        else setFresh(true);
      });
    if (!loadTry) {
      dispatch(fetchAllInterests());
      if (!codes.length) dispatch(fetchAllCodes());
    }
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, email, loadTry]);

  const sportCodes = useMemo(() => codes.filter((c) => c.type === "sport"), [codes]);
  const opts = useMemo(() => {
    const sorted = (t) => codes.filter((c) => c.type === t).sort((x, y) => String(x.name).localeCompare(String(y.name)));
    const lvl = athlete?.levelOfPlay;
    const schoolType = lvl === 1 ? "HighSchool" : lvl === 2 ? "CollegeUniversity" : null;
    const schools = codes.filter((c) => c.type === "school" && (!schoolType || c.options?.type === schoolType)).map((c) => c.name).filter(Boolean);
    const sport = sportCodes.find((c) => c.value === parseInt(athlete?.primarySport, 10));
    return {
      sports: sportCodes, genders: codes.filter((c) => c.type === "gender"), countries: sorted("country"), states: sorted("state"), levels: codes.filter((c) => c.type === "levelOfPlay"),
      positions: (sport?.positions || []).map((p) => ({ value: p.value, label: p.name })),
      otherSports: sportCodes.filter((s) => s.value !== athlete?.primarySport).map((s) => ({ value: s.value, label: s.name })),
      schools, teams: codes.filter((c) => c.type === "team").map((c) => c.name).filter(Boolean),
      highSchools: codes.filter((c) => c.type === "school" && c.options?.type === "HighSchool").map((c) => c.name).filter(Boolean),
      colleges: codes.filter((c) => c.type === "school" && c.options?.type === "CollegeUniversity").map((c) => c.name).filter(Boolean),
    };
  }, [codes, sportCodes, athlete?.levelOfPlay, athlete?.primarySport]);

  // One-time hydrate once this visit's profile load and the code lists are
  // both here, and only from the document this editor is for (the signed-in
  // account's own, or the athlete a Circle member opened).
  useEffect(() => {
    if (athlete || !fresh || !Object.keys(athleteInfo).length || !codes.length) return;
    if (!sameEmail(athleteInfo.email, email)) return;
    const next = hydrate(athleteInfo, sportCodes);
    setAthlete(next);
    const { names: loaded, unresolved } = loadedNames(athleteInfo, next, codeMap);
    unresolvedRef.current = unresolved;
    touchedRef.current = new Set();
    setNamesState(loaded);
  }, [athlete, fresh, athleteInfo, email, codes.length, codeMap, sportCodes]);

  // Cerebro position match, once positions for the sport are known (only
  // from a bio the server trusts).
  useEffect(() => {
    if (!athlete || positionsFilledRef.current) return;
    const cerebroPos = trustedBio(athleteInfo).position;
    if (!cerebroPos || !opts.positions.length || (Array.isArray(athlete.position) && athlete.position.length)) return;
    const matched = matchCerebroPosition(cerebroPos, opts.positions);
    positionsFilledRef.current = true;
    if (matched.length) setAthlete((prev) => ({ ...prev, position: matched }));
  }, [athlete, athleteInfo, opts.positions]);

  const buildPayload = (a, n) => {
    const { cerebroBio, _id, userID, avatar, ...rest } = a;
    SERVER_OWNED.forEach((k) => delete rest[k]);
    const payload = {
      ...rest, email: a.email || email, levelOfPlay: a.levelOfPlay || 0, showContactInfo: a.showContactInfo ? "true" : "false",
      currentTeam: n.clubTeam, previousSchool: n.school, lastHighSchool: n.lastHighSchool,
      previousClubTeams: n.previousClubTeams, previousColleges: n.previousColleges, previousHighSchools: n.previousHighSchools,
    };
    Object.entries(NAME_FIELDS).forEach(([key, field]) => {
      if (unresolvedRef.current.has(key) && !touchedRef.current.has(key)) delete payload[field];
    });
    // A last high school is history the server takes as the athlete's once
    // a save sends it: a blank one they never touched (nothing found, or a
    // level that does not ask for it) is not sent.
    if (!String(n.lastHighSchool || "").trim() && !touchedRef.current.has("lastHighSchool")) delete payload.lastHighSchool;
    if (editingOther) SELF_ONLY_FIELDS.forEach((k) => delete payload[k]);
    return payload;
  };

  const save = useCallback(async () => {
    clearTimeout(timerRef.current);
    // One request at a time. Each save sends the whole profile, so a save
    // still on its way lands first and an older copy never lands after a
    // newer one. The copy is read once the wait is over.
    while (busyRef.current) await inFlightRef.current;
    const { athlete: a, names: n } = latest.current;
    if (!a) return false;
    if (a.height > 100) { setStatus("error"); setError("Height should be in inches, not cm."); return false; }
    dirtyRef.current = false;
    setStatus("saving");
    try {
      const request = Promise.resolve(saveProfileDoc(buildPayload(a, n)));
      busyRef.current = true;
      const settled = request.finally(() => { busyRef.current = false; });
      // Leaving mid-save and queued saves wait on this, so it never rejects.
      inFlightRef.current = settled.then(() => true, () => false);
      await settled;
      savedOnceRef.current = true;
      setStatus(dirtyRef.current ? "dirty" : "saved");
      setError("");
      setFieldErrors({});
      return true;
    } catch (e) {
      setStatus("error");
      setError(e.message || "Could not save.");
      setFieldErrors(e.fields && typeof e.fields === "object" ? e.fields : {});
      return false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const markDirty = useCallback(() => {
    dirtyRef.current = true;
    setStatus("dirty");
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(save, AUTOSAVE_MS);
  }, [save]);
  const set = useCallback((patch) => {
    setAthlete((prev) => ({ ...prev, ...patch }));
    // Editing a field the server refused clears its message.
    setFieldErrors((prev) => (Object.keys(patch).some((k) => k in prev)
      ? Object.fromEntries(Object.entries(prev).filter(([k]) => !(k in patch)))
      : prev));
    markDirty();
  }, [markDirty]);
  // set(), then save at once instead of after the autosave pause. Resolves
  // true once the server has the change and false when it refused it (the
  // footer shows why). save() sends one request at a time, so a save already
  // on its way lands first and an older copy never lands after this one. The
  // socials lookup waits on it, since the scrape reads the links the server has.
  const setAndSave = useCallback((patch) => {
    set(patch);
    // save() reads latest.current, which only the next render refreshes.
    if (latest.current.athlete) latest.current = { ...latest.current, athlete: { ...latest.current.athlete, ...patch } };
    // Also cancels the autosave set() just armed.
    return save();
  }, [set, save]);
  const setNames = useCallback((patch) => {
    Object.keys(patch).forEach((k) => touchedRef.current.add(k));
    setNamesState((prev) => ({ ...prev, ...patch }));
    markDirty();
  }, [markDirty]);

  useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);

  // Leaving: flush anything pending and wait for a save still in flight, then
  // refresh the score and the shared profile copy (the header, My Profile) in
  // the background. The next visit waits for all of it before it loads.
  useEffect(() => () => {
    clearTimeout(timerRef.current);
    const wasDirty = dirtyRef.current;
    const inFlight = inFlightRef.current;
    const saves = [inFlight, wasDirty ? save() : null].filter(Boolean);
    const flush = Promise.all(saves).then((oks) => oks.every(Boolean)).catch(() => false);
    pendingFlush = flush;
    // A Circle member's save is rescored by the server; recompute-self is
    // the athlete's own call, and there is no own profile copy to refresh.
    if (!editingOther && (savedOnceRef.current || wasDirty || inFlight)) {
      flush.then(() => {
        recomputeSelf().catch(() => {});
        // Only for the account that is still signed in on this tab.
        if (sameEmail(localStorage.getItem("myEmail"), email)) dispatch(fetchAthleteProfileByEmail(email));
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sections = editingOther ? SECTIONS.filter((s) => !SELF_ONLY_SECTIONS.has(s.id)) : SECTIONS;
  // On phones no section (or one this editor does not have) is the hub.
  const wanted = SECTION_ALIASES[params.get("section")] || params.get("section");
  const current = sections.find((s) => s.id === wanted) || (phone ? null : sections[0]);

  // Phone screens: a section opens at the top, and the hub comes back where
  // the athlete left it when they opened that section. When the screen
  // changes (not on the first one), focus moves to the new screen's
  // visually hidden heading, so screen readers announce it and keyboard
  // focus is not left on a control that is gone.
  const phoneScreen = phone ? current?.id || "hub" : "";
  const screenRef = useRef(phoneScreen);
  const hubScrollRef = useRef(null);
  const headingRef = useRef(null);
  useLayoutEffect(() => {
    const before = screenRef.current;
    screenRef.current = phoneScreen;
    if (!phoneScreen) return;
    // Focus before scrolling, so a browser that scrolls to the focused
    // heading anyway (no preventScroll) still ends at the place set below.
    if (before && before !== phoneScreen) headingRef.current?.focus({ preventScroll: true });
    if (phoneScreen === "hub" && hubScrollRef.current !== null) {
      scrollToY(hubScrollRef.current);
      hubScrollRef.current = null;
    } else scrollTop();
  }, [phoneScreen]);
  // A change refused after the athlete left its section shows its note at
  // the top of the hub: bring it into view instead of leaving it above the
  // restored place, where it would go unseen.
  const hubRefused = phoneScreen === "hub" && status === "error";
  useLayoutEffect(() => {
    if (hubRefused) scrollTop();
  }, [hubRefused]);

  const open = (id) => {
    // The hub's place, for when this section goes back to it.
    if (phone && !current) hubScrollRef.current = pageScrollY();
    if (dirtyRef.current) save();
    // A phone section remembers it came from the hub, so going back pops it.
    if (phone) setParams({ section: id }, { state: { fromHub: true } });
    else setParams({ section: id });
    window.scrollTo({ top: 0 });
  };

  // Phone navigation. Back on the hub leaves the editor (the page before it,
  // else My Profile; a Circle member goes back to their Circle). Back on a
  // section returns to the hub, saving what is pending on the way; the hub
  // stays mounted, so the save carries on there (a failed one is retried),
  // and the hub says so when it is refused.
  const leaveEditor = () => {
    if (editingOther && backTo) navigate(backTo);
    else if (canStepBack(enteredFirstRef.current)) navigate(-1);
    else navigate(editingOther ? "/circle" : "/athlete/profile");
  };
  const goHub = () => {
    if (location.state?.fromHub) navigate(-1);
    else setParams({}, { replace: true });
  };
  const backToHub = () => {
    if (dirtyRef.current || status === "error") save();
    goHub();
  };
  // Done: save anything pending (or retry a failed save), or wait for the
  // save on its way, and return to the hub once the server has it. When it
  // is refused the section stays open and the bar shows why.
  const done = async () => {
    if (doneBusy) return;
    setDoneBusy(true);
    let ok = true;
    if (dirtyRef.current || status === "error") ok = await save();
    else if (busyRef.current && inFlightRef.current) ok = await inFlightRef.current;
    if (!aliveRef.current) return;
    setDoneBusy(false);
    if (ok) goHub();
  };

  const onAvatarUpload = async (file) => {
    if (file.size > 5 * 1024 * 1024) { toast.error("That image is over 5 MB. Choose a smaller one."); return; }
    setAvatarBusy(true);
    try {
      const out = await uploadAvatar(await compressImage(file), athlete.email || email);
      if (out?.avatar) { setAthlete((prev) => ({ ...prev, avatar: out.avatar })); savedOnceRef.current = true; toast.success("Photo updated."); }
      else toast.error(out?.error || "Upload failed. Try again.");
    } catch { toast.error("Upload failed or timed out. Try a smaller photo."); } finally { setAvatarBusy(false); }
  };
  // Remove asks first (ConfirmModal); only the athlete removes their photo.
  const onAvatarDelete = () => { if (!editingOther) setConfirmingRemove(true); };
  const removePhoto = async () => {
    setAvatarBusy(true);
    try {
      await deleteAvatar(athlete.email || email);
      setAthlete((prev) => ({ ...prev, avatar: "" }));
      setConfirmingRemove(false);
    } catch { toast.error("Could not remove the photo."); } finally { setAvatarBusy(false); }
  };
  // A team was added beside the main one (Add a team, or a code for another
  // team): the reloaded profile's identity may now show it, as a club or as
  // the athlete's school team. Blank names the athlete has not touched are
  // filled from it as a fresh load would fill them (a single name is saved
  // with the next save, a history list stays display only); nothing the
  // athlete typed changes.
  const fillBlankNames = (doc) => {
    const identity = doc?.identity?.current ? doc.identity : null;
    if (!identity) return;
    const trusted = doc.bioTrusted === true;
    const singles = identitySchoolNames(latest.current.athlete || doc, identity, trusted);
    const lists = identityHistoryNames(identity, trusted);
    const n = latest.current.names;
    const open = (k) => !touchedRef.current.has(k) && !unresolvedRef.current.has(k);
    const patch = {};
    SINGLE_NAMES.forEach((k) => {
      if (!String(n[k] || "").trim() && open(k) && singles[k]) patch[k] = singles[k];
    });
    // As on a fresh load: with no level yet, the Team is shown, not sent.
    if (patch.school && !Number((latest.current.athlete || doc).levelOfPlay)) unresolvedRef.current.add("school");
    HISTORY_LISTS.forEach(([k]) => {
      if (!(n[k] || []).length && !touchedRef.current.has(k) && lists[k].length) {
        patch[k] = lists[k];
        unresolvedRef.current.add(k);
      }
    });
    if (Object.keys(patch).length) setNamesState((prev) => ({ ...prev, ...patch }));
  };

  // A new main team (Make primary on the Teams card): the server filed it by
  // its kind and may have moved the old one (a transfer's college goes to
  // the previous colleges), so every Teams field shows what a fresh load of
  // `doc` would, and none is sent until the athlete edits it again (saves
  // would otherwise send back the names from before the switch). A field
  // edited since the switch keeps what was typed. `a` is the editor's copy
  // with the reloaded team fields and level.
  const refileNames = (doc, a) => {
    const { names: loaded } = loadedNames(doc, a, codeMap);
    const patch = Object.fromEntries(Object.entries(loaded).filter(([k]) => !touchedRef.current.has(k)));
    if (Object.keys(patch).length) setNamesState((prev) => ({ ...prev, ...patch }));
  };

  // Add a team (the Teams card) added a team beside the main one, or Make
  // primary made another team the main one (`primaryChanged`). Team fields
  // are the server's, so nothing is saved: the profile is read again for the
  // team list, the identity and the names it now gives. After an add, blank
  // names fill (fillBlankNames); after a new main team every Teams field
  // shows what the server filed (refileNames) and the level is the server's
  // once it has one, so the next save does not send the old one back over
  // what the switch set (a college team makes an athlete with no level, or
  // with a graduated high school class, College). A level picked on the
  // Sport screen was saved on the way here, so the server has it too.
  const onTeamAdded = async (data) => {
    const primaryChanged = data?.primaryChanged === true;
    // From now until the reload is in, no save sends a Teams name: the
    // switch may have moved it on the server.
    if (primaryChanged) {
      Object.keys(NAME_FIELDS).forEach((k) => {
        touchedRef.current.delete(k);
        unresolvedRef.current.add(k);
      });
    }
    const doc = await dispatch(fetchAthleteProfileByEmail(email));
    const reloaded = doc && typeof doc === "object" && sameEmail(doc.email, email) ? doc : null;
    if (!reloaded || !aliveRef.current) return;
    // The new main team may have made a school or team code.
    if (primaryChanged) dispatch(fetchAllCodes());
    const fold = (prev) => {
      const next = { ...prev };
      TEAM_LINK_FIELDS.forEach((k) => { if (k in reloaded) next[k] = reloaded[k]; });
      if (!primaryChanged) return next;
      SLOT_FIELDS.forEach((k) => { if (k in reloaded) next[k] = reloaded[k]; });
      if (levelOf(reloaded)) next.levelOfPlay = reloaded.levelOfPlay;
      // A sport or gender the team gave a profile that had none.
      ["primarySport", "gender"].forEach((k) => { if (!prev[k] && reloaded[k]) next[k] = reloaded[k]; });
      return next;
    };
    setAthlete((prev) => (prev ? fold(prev) : prev));
    if (primaryChanged) refileNames(reloaded, fold(latest.current.athlete || reloaded));
    else fillBlankNames(reloaded);
  };

  // Just before a team is linked in a way that makes the server write the
  // Teams fields (a Club or Team field's pick, the first team added, a spot
  // on the main team, Make primary): the save waiting to go is sent first,
  // with the names as the editor has them, and a save already on its way
  // lands first, so a copy of the names from before never lands after the
  // server's write.
  // From then until the profile is read again (onTeamAdded) no save sends a
  // Teams name; a field the athlete edits again is sent again. Resolves to
  // a function that puts the names back as they were, for a write the
  // server refused (its fields are as before, and saved as before).
  const beforeTeamWrite = async () => {
    if (dirtyRef.current) await save();
    else if (busyRef.current && inFlightRef.current) await inFlightRef.current;
    const touched = new Set(touchedRef.current);
    const unresolved = new Set(unresolvedRef.current);
    Object.keys(NAME_FIELDS).forEach((k) => {
      touchedRef.current.delete(k);
      unresolvedRef.current.add(k);
    });
    return () => {
      Object.keys(NAME_FIELDS).forEach((k) => {
        if (touched.has(k)) touchedRef.current.add(k);
        if (!unresolved.has(k)) unresolvedRef.current.delete(k);
      });
    };
  };

  // A Club or Team field linked a team (athletes/teams/slot/, notes of
  // 10/19): the server made it a binding (the main team when there was
  // none), wrote the field from it, moved the value it replaced to the
  // field's history and filled the sport, gender and level by the pre-fill
  // rules. Team fields are the server's, so nothing is saved: the profile is
  // read again and every Teams field shows what the server filed, as after
  // Make primary (onTeamAdded with primaryChanged). `label` names the field.
  const onTeamSlotSet = async (data, label, team) => {
    const teams = Array.isArray(data?.teams) ? data.teams : [];
    const hit = teams.find((t) => t && t.teamId && t.teamId === data?.teamId && (!t.league || !data?.league || t.league === data.league));
    const name = teamTitle(hit || team || {}) || "The team";
    toast.success(`${name} is now your ${String(label || "team").toLowerCase()}.`);
    const note = fillNoteOf(data);
    if (note) toast.info(note);
    await onTeamAdded({ ...data, primaryChanged: true });
  };

  // A coach or team code was confirmed. The link set the team fields on the
  // server, so fold the reloaded ones into the editor (without touching the
  // athlete's own edits) and show the team where the link filed it, by the
  // server's rule (onboarding_views.college_school_set): a college team, for
  // an athlete not set as HS or Pro, as their college Team when they had
  // none (with the College level the link gave an athlete who had no
  // level), any other team it gave a team-less athlete in Club. A college
  // team never shows as the club. Whether the team is a college is the
  // server's `kind` for it (the reloaded team list's, else the link's),
  // never a guess from its labels; the names are the reloaded identity's (a
  // college by its institution). Those names are for display only: the link
  // already saved them, and sending a name back would look it up again and
  // could land on another code, so saves leave the field out until the
  // athlete edits it. A code that added a team beside the main one fills
  // blank names from the reloaded identity (fillBlankNames).
  const onTeamLinked = async (data) => {
    const before = latest.current.athlete;
    const primaryLink = !data?.addedAsAdditionalTeam;
    // The server's rule reads the level it has (the profile this visit
    // loaded), not one the hub only proposed from Cerebro, which no save has
    // sent yet: with none stored, a college team makes the athlete College.
    const serverHadNoLevel = sameEmail(athleteInfo?.email, email) && !levelOf(athleteInfo);
    // Covers a save that runs while the profile reloads, too: neither field
    // is sent over what the link may have written.
    const schoolLeftOut = unresolvedRef.current.has("school");
    if (primaryLink && !touchedRef.current.has("clubTeam")) unresolvedRef.current.add("clubTeam");
    if (primaryLink && !touchedRef.current.has("school")) unresolvedRef.current.add("school");
    const doc = await dispatch(fetchAthleteProfileByEmail(email));
    const reloaded = doc && typeof doc === "object" && sameEmail(doc.email, email) ? doc : null;
    // The link may have created the team's code; reload the list so it resolves.
    dispatch(fetchAllCodes());
    // Without the reload, the linked team's level or league says whether the
    // college rule applied: a college team for an athlete not set as HS or Pro.
    const sameTeam = (t) => t && (t.teamId && data?.teamId
      ? t.teamId === data.teamId && (!t.league || !data.league || t.league === data.league)
      : t.teamName === data?.teamName);
    const linked = (Array.isArray(data?.teams) ? data.teams : []).find(sameTeam);
    const reloadedTeam = (Array.isArray(reloaded?.allTeams) ? reloaded.allTeams : []).find(sameTeam);
    const linkedKind = reloadedTeam?.kind || linked?.kind || "";
    const collegeRule = linkedKind === "college" && [0, 2].includes(levelOf(before));
    // Filed as the college Team: the reloaded profile has a school
    // the link gave it (or, without the reload, the rule applied to a profile
    // with no school).
    const filedCollege = primaryLink && (reloaded
      ? Number(reloaded.previousSchool) > 0 && Number(reloaded.previousSchool) !== Number(before?.previousSchool)
      : collegeRule && !Number(before?.previousSchool));
    setAthlete((prev) => {
      if (!prev) return prev;
      const next = { ...prev, status: "verified" };
      if (Array.isArray(data?.teams)) next.verifiedTeams = data.teams;
      if (reloaded) TEAM_LINK_FIELDS.forEach((k) => { if (k in reloaded) next[k] = reloaded[k]; });
      if (reloaded && filedCollege) next.previousSchool = reloaded.previousSchool;
      // The College level the link gave an athlete with no level stored:
      // kept, so the next save does not send the blank level (or the one the
      // hub proposed from Cerebro) back over it and the college does not read
      // as a high school.
      if (reloaded && primaryLink && levelOf(reloaded) === 2 && (!levelOf(prev) || serverHadNoLevel)) next.levelOfPlay = reloaded.levelOfPlay;
      return next;
    });
    const tookTeam = reloaded
      ? Number(reloaded.currentTeam) > 0 && Number(reloaded.currentTeam) !== Number(before?.currentTeam)
      : primaryLink && !Number(before?.currentTeam) && !collegeRule;
    // The names where the reloaded identity shows them, else the team's.
    const shown = reloaded?.identity?.current ? identitySchoolNames(reloaded, reloaded.identity, reloaded.bioTrusted === true) : null;
    if (filedCollege && !touchedRef.current.has("school")) {
      const school = shown?.school || data?.teamName;
      if (school) setNamesState((prev) => ({ ...prev, school }));
    } else {
      if (!schoolLeftOut) unresolvedRef.current.delete("school");
      // A school the identity shows that the link did not file itself (a
      // college a link put in the Club field before the dev call): shown in
      // a blank field, as a fresh load would, and saved with the next save.
      if (shown?.school && primaryLink && !touchedRef.current.has("school")) {
        setNamesState((prev) => (prev.school ? prev : { ...prev, school: shown.school }));
      }
    }
    if (tookTeam && !touchedRef.current.has("clubTeam")) {
      const club = shown ? shown.clubTeam : data?.teamName;
      if (club || shown) setNamesState((prev) => ({ ...prev, clubTeam: club || "" }));
      unresolvedRef.current.add("clubTeam");
    }
    if (!primaryLink && reloaded) fillBlankNames(reloaded);
    toast.success("Team verified.");
  };

  // The Teams card added or removed a team: keep the verified list the code
  // box reads, and the profile status when the server sent one (removing the
  // last verified team makes it unverified), in step. Team fields are the
  // server's, so nothing is saved.
  const onTeamsChanged = (teams, status) => {
    setAthlete((prev) => (prev ? { ...prev, verifiedTeams: (teams || []).filter((t) => t.verified), ...(status ? { status } : {}) } : prev));
  };

  // A phone screen always shows its header first, so there is a way back.
  const framed = (node) => (phone ? <PhoneEditFrame title={current?.label || "Edit profile"} onBack={leaveEditor}>{node}</PhoneEditFrame> : node);
  if (!email) return framed(<div className="py-10 text-center bm-body-m text-bm-muted">Log in to edit your profile.</div>);
  if (!athlete) {
    if (loadFailed) {
      return framed(
        <div className="flex flex-col items-center gap-4 py-10">
          <ErrorState>{editingOther ? "We could not load this profile." : "We could not load your profile."}</ErrorState>
          <Btn variant="secondary" onClick={() => setLoadTry((n) => n + 1)}>Try again</Btn>
        </div>,
      );
    }
    return framed(<Loading label={editingOther ? "Loading the profile..." : "Loading your profile..."} />);
  }

  const progress = current ? sectionProgress(current, athlete, names) : null;
  const hub = hubProgress(athlete, names);
  const after = current ? nextSection(current.id) : null;
  const next = after && sections.includes(after) ? after : null;
  const fullName = `${athlete.name || ""} ${athlete.surname || ""}`.trim();
  const firstName = athlete.name || fullName || "the athlete";
  const whose = editingOther ? (fullName ? `${fullName}'s profile` : "This profile") : "Your profile";
  const permissionLabel = PERMISSION_LABELS[circlePermission] || "";
  const props = { a: athlete, names, set, setAndSave, setNames, opts, interestsKnown, avatarBusy, onAvatarUpload, onAvatarDelete, onTeamLinked, onTeamsChanged, onTeamAdded, onTeamSlotSet, beforeTeamWrite, complete: !!progress?.complete, fieldErrors, editingOther };
  const body = {
    photo: <PhotoBioSection {...props} />, personal: <PersonalSection {...props} />, sport: <SportSection {...props} />, school: <SchoolSection {...props} />,
    socials: <SocialsSection {...props} />, academics: <AcademicsSection {...props} />, honors: <HonorsSection {...props} />, work: <WorkSection {...props} />, privacy: <PrivacySection {...props} />,
  }[current?.id];

  const removeConfirm = confirmingRemove && (
    <ConfirmModal
      title="Remove profile photo"
      body="Your photo comes off your profile and coaches and brands see your initials instead. Without a photo you are not listed in the index. You can upload a new one anytime."
      ackLabel="I understand my photo will be removed."
      confirmLabel="Remove photo"
      tone="danger"
      busy={avatarBusy}
      onConfirm={removePhoto}
      onClose={() => { if (!avatarBusy) setConfirmingRemove(false); }}
    />
  );

  if (phone) {
    return (
      <>
        {current ? (
          <PhoneSection section={current} progress={progress} status={status} error={error} onBack={backToHub} onDone={done} doneBusy={doneBusy} headingRef={headingRef}>
            {body}
          </PhoneSection>
        ) : (
          <PhoneHub
            headingRef={headingRef}
            sections={sections}
            athlete={athlete}
            names={names}
            codeMap={codeMap}
            hub={hub}
            whose={whose}
            onOpen={open}
            onBack={leaveEditor}
            saveError={status === "error" ? error || "Could not save." : ""}
            context={editingOther ? (
              <div className="flex items-start gap-3 rounded-[16px] border border-bm-mutedbg bg-white px-[18px] py-4">
                <Avatar src={athlete.avatar} name={athlete.name} surname={athlete.surname} size={40} tone="outline" />
                <div className="flex min-w-0 flex-1 flex-col items-start gap-[2px] break-words">
                  <span className="bm-h-s text-bm-ink">Editing {fullName ? `${fullName}'s` : "this athlete's"} profile</span>
                  <span className="bm-body-m text-bm-muted">
                    {permissionLabel ? `${firstName} gave you ${permissionLabel} access in their Circle.` : `You are editing as a member of ${firstName}'s Circle.`}
                    {` Privacy settings, teams and removing the photo stay with ${firstName}.`}
                  </span>
                  {permissionLabel && <span className="pt-2"><Badge tone="outline">{permissionLabel} access</Badge></span>}
                </div>
              </div>
            ) : null}
          />
        )}
        {removeConfirm}
      </>
    );
  }

  return (
    <div>
      {editingOther ? (
        <>
          <PageHeader
            eyebrow="Circle · Editing athlete"
            title="Edit profile"
            subtitle={`Everything coaches, recruiters and brands see on ${firstName}'s profile. Changes save automatically.`}
            actions={backTo ? <Btn variant="secondary" to={backTo}>Back to My Circle</Btn> : null}
          />
          <Card padding="px-5 py-4" className="flex flex-wrap items-center gap-4">
            <Avatar src={athlete.avatar} name={athlete.name} surname={athlete.surname} size={40} tone="outline" />
            <div className="flex min-w-0 flex-1 flex-col gap-[2px]">
              <span className="bm-h-s text-bm-ink">Editing {fullName ? `${fullName}'s` : "this athlete's"} profile</span>
              <span className="bm-body-m text-bm-muted">
                {permissionLabel ? `${firstName} gave you ${permissionLabel} access in their Circle.` : `You are editing as a member of ${firstName}'s Circle.`}
                {` Privacy settings, teams and removing the photo stay with ${firstName}.`}
              </span>
            </div>
            {permissionLabel && <Badge tone="outline">{permissionLabel} access</Badge>}
          </Card>
        </>
      ) : (
        <PageHeader eyebrow={`Editing athlete · ${fullName || "you"}`} title="Edit profile" subtitle="Everything coaches, recruiters and brands see. Changes save automatically." />
      )}
      <div className="flex flex-col gap-6 pb-14 pt-[28px] lg:flex-row lg:gap-10">
        <aside className="flex w-full shrink-0 flex-col gap-5 lg:w-[300px]">
          <ProfileStrength hub={hub} whose={whose} onOpen={open} />
          {GROUPS.filter((g) => sections.some((s) => s.group === g)).map((g) => (
            <div key={g} className="flex flex-col gap-1">
              <div className="pb-1 pl-3"><span className="bm-overline !text-bm-tertiary">{g}</span></div>
              {sections.filter((s) => s.group === g).map((s) => {
                const active = s.id === current.id;
                const p = sectionProgress(s, athlete, names);
                return (
                  <button key={s.id} type="button" onClick={() => open(s.id)} className={`flex h-[52px] w-full items-center gap-3 rounded-[12px] px-3 py-[10px] text-left ${active ? "border border-bm-border bg-white" : "hover:bg-bm-subtle"}`}>
                    <NavIcon src={s.icon} active={active} />
                    <span className={`flex-1 bm-label-l ${active ? "text-bm-ink" : "text-bm-muted"}`}>{s.label}</span>
                    {!s.setting && <Ring size={32} pct={p.pct} complete={p.complete} />}
                  </button>
                );
              })}
            </div>
          ))}
        </aside>
        <section className="flex w-full min-w-0 flex-col gap-6 lg:w-[720px]">
          <SectionHead section={current} progress={progress} status={status} error={error} />
          {body}
          <SectionFooter onSave={save} saving={status === "saving"} next={next} onNext={async () => { if (dirtyRef.current || status === "error") { const ok = await save(); if (!ok) return; } open(next.id); }} />
        </section>
      </div>
      {removeConfirm}
    </div>
  );
};

export default AthleteEditHub;
